import { SortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import {
    defaultPaginationInfo,
    PaginationInfo
} from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';

export interface SearchParams {
    [param: string]: string | string[];
}

export interface SearchParamsPostBody {
    Q: string;
    Sq?: string;
    Ftq?: string;
    StartIndex?: string;
    NrOfResults?: string;
    Sort?: string[];
    Direction?: string;
    Fields?: DottedKey[];
}

import { HttpParams } from '@angular/common/http';
import { ApiQuery } from '../../api/entities/api-query.entity';
import { ApiQueryBuilder } from '../../api/helpers/api-query-builder.helper';
import { DottedKey } from '../../field-definitions/entities/field-definition.entity';
import { CustomHttpParamEncoder } from '../../http/entities/custom-http-param-encoder';

export class RecordSearchRequest {
    private readonly Q: string;
    private readonly Sq?: string;
    private readonly Ftq?: string;
    private readonly StartIndex?: number;
    private readonly NrOfResults?: number;
    private readonly Sort: string[];
    private readonly Direction?: string;
    private readonly Fields?: DottedKey[];

    constructor(apiQuery: ApiQuery, paginationInfo?: PaginationInfo, sortInfo?: SortInfo) {
        this.Q = ApiQueryBuilder.queryToLuceneQuery(apiQuery);
        this.Sq = ApiQueryBuilder.buildSemanticSearchQuery(apiQuery);
        this.Ftq = ApiQueryBuilder.buildFullTextSearchQuery(apiQuery);

        this.StartIndex = paginationInfo ? paginationInfo.page * paginationInfo.resultsPerPage : 0;

        if (paginationInfo) {
            this.NrOfResults = paginationInfo.resultsPerPage;
            this.StartIndex = paginationInfo.page * paginationInfo.resultsPerPage;
        }

        if (sortInfo) {
            this.Sort = [`${sortInfo.field}`];
            this.Direction = sortInfo.direction;
        }

        if (apiQuery.fieldSelect) {
            this.Fields = apiQuery.fieldSelect;
        }
    }

    // #region Getters
    getQuery(): string {
        return this.Q;
    }

    getSemanticQuery(): string {
        return this.Sq ?? '';
    }

    getStartIndex(): number {
        return this.StartIndex ?? defaultPaginationInfo.page;
    }

    getNrOfResults(): number {
        return this.NrOfResults ?? defaultPaginationInfo.resultsPerPage;
    }

    getSort(): string[] {
        return this.Sort;
    }

    // #endregion

    // #region Convertors
    toPostBody(): SearchParamsPostBody {
        return {
            Q: this.Q,
            Sq: this.Sq,
            Ftq: this.Ftq,
            StartIndex: this.StartIndex?.toString(),
            NrOfResults: this.NrOfResults?.toString(),
            Sort: this.Sort,
            Direction: this.Direction,
            ...(this.Fields ? { Fields: this.Fields } : {})
        };
    }

    toSimilaritySearchPostBody(): SearchParamsPostBody {
        return {
            Q: this.Q,
            StartIndex: this.StartIndex?.toString(),
            NrOfResults: this.NrOfResults?.toString(),
            ...(this.Fields ? { Fields: this.Fields } : {})
        };
    }

    toHttpParams(): HttpParams {
        let httpParams: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() });

        httpParams = httpParams.append('q', this.Q);

        if (this.Sq) {
            httpParams = httpParams.append('sq', this.Sq);
        }

        if (this.Ftq) {
            httpParams = httpParams.append('ftq', this.Ftq);
        }

        if (this.StartIndex) {
            httpParams = httpParams.append('startIndex', this.StartIndex.toString());
        }

        if (this.NrOfResults) {
            httpParams = httpParams.append('nrOfResults', this.NrOfResults.toString());
        }

        if (this.Sort?.length > 0) {
            httpParams = httpParams.append('sort', this.Sort.join('&'));
        }

        if (this.Direction) {
            httpParams = httpParams.append('direction', this.Direction);
        }

        if (this.Fields) {
            this.Fields.forEach(field => (httpParams = httpParams.append('fields', field)));
        }

        return httpParams;
    }

    toPaginationInfo(): PaginationInfo {
        return {
            page: Math.floor(this.getStartIndex() / this.getNrOfResults()) ?? 0,
            resultsPerPage: this.getNrOfResults()
        };
    }
    // #endregion
}
